import React from 'react';
import { getVisiblePages } from '../../utils/pagination';

const btnBase = {
  minWidth: '36px',
  height: '36px',
  padding: '0 10px',
  border: '1px solid #e2e8f0',
  borderRadius: '8px',
  background: '#ffffff',
  color: '#475569',
  cursor: 'pointer',
  fontSize: '14px',
  fontWeight: 500,
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  gap: '4px',
};

/**
 * Shared pagination with numbered page buttons.
 * @param {number} page - current page (0-based by default)
 * @param {number} totalPages
 * @param {number} [totalElements]
 * @param {number} [pageSize=10]
 * @param {(pageZeroBased:number)=>void} onPageChange
 * @param {0|1} [pageBase=0] - if 1, `page`/`onPageChange` use 1-based numbers
 * @param {boolean} [showSummary=true]
 */
export default function PaginationBar({
  page = 0,
  totalPages = 1,
  totalElements,
  pageSize = 10,
  onPageChange,
  pageBase = 0,
  showSummary = true,
  style = {},
}) {
  const total = Math.max(1, Number(totalPages) || 1);
  const currentZero = pageBase === 1
    ? Math.max(0, (Number(page) || 1) - 1)
    : Math.max(0, Number(page) || 0);
  const currentOne = currentZero + 1;
  const hasPrev = currentZero > 0;
  const hasNext = currentZero < total - 1;
  const items = getVisiblePages(currentOne, total, 1);

  const go = (zeroBased) => {
    if (!onPageChange) return;
    const clamped = Math.min(Math.max(0, zeroBased), total - 1);
    onPageChange(pageBase === 1 ? clamped + 1 : clamped);
  };

  const summary = (() => {
    if (!showSummary || totalElements == null) return null;
    const totalItems = Number(totalElements) || 0;
    if (totalItems <= 0) return 'Showing 0 entries';
    const start = currentZero * pageSize + 1;
    const end = Math.min((currentZero + 1) * pageSize, totalItems);
    return `Showing ${start} to ${end} of ${totalItems} entries`;
  })();

  return (
    <div
      style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        flexWrap: 'wrap',
        gap: '12px',
        padding: '16px 0',
        ...style,
      }}
    >
      <div style={{ fontSize: '14px', color: '#64748b', fontWeight: 500 }}>
        {summary}
      </div>
      <div style={{ display: 'flex', gap: '6px', alignItems: 'center', flexWrap: 'wrap' }}>
        <button
          type="button"
          onClick={() => go(currentZero - 1)}
          disabled={!hasPrev}
          style={{
            ...btnBase,
            background: !hasPrev ? '#f8fafc' : '#ffffff',
            color: !hasPrev ? '#94a3b8' : '#475569',
            cursor: !hasPrev ? 'not-allowed' : 'pointer',
          }}
        >
          ‹ Prev
        </button>

        {items.map((item, idx) =>
          item === '...' ? (
            <span key={`e-${idx}`} style={{ padding: '0 4px', color: '#94a3b8', fontSize: '14px' }}>
              …
            </span>
          ) : (
            <button
              type="button"
              key={item}
              onClick={() => go(item - 1)}
              style={{
                ...btnBase,
                background: item === currentOne ? '#2563eb' : '#ffffff',
                color: item === currentOne ? '#ffffff' : '#475569',
                borderColor: item === currentOne ? '#2563eb' : '#e2e8f0',
                fontWeight: item === currentOne ? 700 : 500,
              }}
            >
              {item}
            </button>
          )
        )}

        <button
          type="button"
          onClick={() => go(currentZero + 1)}
          disabled={!hasNext}
          style={{
            ...btnBase,
            background: !hasNext ? '#f8fafc' : '#ffffff',
            color: !hasNext ? '#94a3b8' : '#475569',
            cursor: !hasNext ? 'not-allowed' : 'pointer',
          }}
        >
          Next ›
        </button>
      </div>
    </div>
  );
}
